import { useState } from 'react'
import { ChevronDown, Check, Briefcase } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useOrgs } from '@/hooks/useOrgs'
import { useSwitchOrg } from '@/hooks/useSwitchOrg'
import type { OrgSummary } from '@/types/onboarding'

interface OrgSwitcherProps {
  isCollapsed: boolean
}

/**
 * The organisation (tenant) control, above the workspace switcher.
 *
 * A person can belong to several organisations — `qyngent.org_members` is
 * genuinely many-to-many — and the qyngent JWT deliberately carries no
 * `org_id` claim, so the backend resolves the tenant per request from the
 * `X-Org-Id` header apiFetch stamps from this selection. Without this control
 * a multi-org user is permanently pinned to the ownership-based default, and
 * someone who only ever accepted an invitation cannot reach the org they
 * joined at all.
 *
 * It renders ONLY when there is more than one org: the single-org majority
 * would otherwise get a control that does nothing, above a workspace switcher
 * that does the same thing at a finer grain. Org is the coarser scope, so it
 * sits above.
 */
export function OrgSwitcher({ isCollapsed }: OrgSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false)
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId)
  const switchOrg = useSwitchOrg()
  const { data } = useOrgs()

  const orgs = data?.organizations ?? []
  // Fall back to the first entry, which is the org the backend resolves when
  // nothing usable is selected — the list is ordered by the same rule.
  const active = orgs.find((o) => o.id === activeOrgId) ?? orgs[0]

  if (orgs.length < 2) return null

  const handleSelect = (org: OrgSummary) => {
    switchOrg(org.id)
    setIsOpen(false)
  }

  const menu = (
    <div
      role="listbox"
      aria-label="Available organizations"
      className="absolute left-3 right-3 top-full mt-1 z-50 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl overflow-hidden"
    >
      {orgs.map((org) => (
        <button
          key={org.id}
          role="option"
          aria-selected={org.id === active?.id}
          onClick={() => handleSelect(org)}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <span className="flex-1 truncate text-left">{org.name}</span>
          {/* The caller's role, so a member knows why an admin action is
              missing once they switch. The owner's own org says nothing —
              it's the unremarkable case. */}
          {org.role && org.role !== 'owner' && (
            <span className="text-[11px] text-gray-500 shrink-0">{org.role}</span>
          )}
          {org.id === active?.id && (
            <Check className="w-3.5 h-3.5 text-lime shrink-0" aria-hidden="true" />
          )}
        </button>
      ))}
    </div>
  )

  if (isCollapsed) {
    return (
      <div className="relative px-3 pt-2" data-testid="org-switcher">
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Switch organization"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          title={active?.name ?? 'Select organization'}
          className="flex items-center justify-center w-full p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
        >
          <Briefcase className="w-4 h-4" aria-hidden="true" />
        </button>
        {isOpen && menu}
      </div>
    )
  }

  return (
    <div className="relative px-3 pt-2" data-testid="org-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Current organization: ${active?.name ?? 'Select organization'}`}
        className="flex items-center w-full gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-left"
      >
        <Briefcase className="w-3.5 h-3.5 text-gray-400 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-xs text-gray-400 truncate">
          {active?.name ?? 'Select organization'}
        </span>
        <ChevronDown
          className={cn('w-3 h-3 text-gray-500 transition-transform', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {isOpen && menu}
    </div>
  )
}
